/**
 * Icon - Wrapper component for inline SVG icons.
 * Provides consistent sizing, accessibility attributes, and a shared viewBox.
 * Pass SVG <path> elements as children to render the desired icon.
 *
 * Uses Heroicons-style 24x24 viewBox with 1.5 stroke width by default.
 *
 * @example
 * <Icon size="md" label="Settings">
 *	 <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c..." />
 * </Icon>
 *
 * // Decorative icon (no label, hidden from screen readers):
 * <Icon size="sm"><path ... /></Icon>
 */
import React from 'react';

// --- Type definitions ---

/** Available icon size presets */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Props for the Icon component.
 * Extends native SVG attributes so callers can pass fill, stroke overrides, etc.
 */
export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
	/** Size preset controlling the width and height — defaults to 'md' */
	size?: IconSize;
	/** SVG path elements to render inside the icon */
	children: React.ReactNode;
	/** Accessible label — when provided, the icon is treated as meaningful content;
	 *	when omitted, the icon is decorative and hidden from assistive technology */
	label?: string;
}

// --- Size mappings ---
// Maps size presets to Tailwind width/height classes.
// Values align with the 4px spacing scale used in the design system.
const sizeMap: Record<IconSize, string> = {
	sm: 'w-16px h-16px',
	md: 'w-20px h-20px',
	lg: 'w-24px h-24px',
	xl: 'w-32px h-32px',
};

const Icon: React.FC<IconProps> = ({ size = 'md', children, label, className = '', ...rest }) => {
	return (
		// Accessibility: aria-hidden=true when no label (decorative icon).
		// When label is provided, role="img" + aria-label make it announced by screen readers.
		// stroke="currentColor" inherits text color from the parent for easy theming.
		<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`icon ${sizeMap[size]} ${className}`} aria-hidden={!label} aria-label={label} role={label ? 'img' : undefined} {...rest} data-ref="atom-icon">
			{children}
		</svg>
	);
};

export default Icon;
